import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import {
  FaStar,
  FaEdit,
  FaTrash,
  FaUser,
  FaPaperPlane,
  FaTimes,
  FaCheck,
  FaRegStar,
  FaStarHalfAlt
} from "react-icons/fa";
import "../styles/Reviews.css";

export default function Reviews({ solicitorId }) {
  const { user, token } = useAuth();

  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState("");
  const [hoverRating, setHoverRating] = useState(0);
  const [loading, setLoading] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editRating, setEditRating] = useState(0);
  const [editReview, setEditReview] = useState("");
  const [editHoverRating, setEditHoverRating] = useState(0);

  useEffect(() => {
    fetchReviews();
  }, [solicitorId]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/reviews/${solicitorId}`
      );
      setReviews(res.data);
    } catch (error) {
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  /* ======================
     CREATE REVIEW
  ====================== */
  const submitReview = async () => {
    if (!rating || rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    if (!review.trim()) {
      toast.error("Please write your review");
      return;
    }

    try {
      await axios.post(
        `${API_BASE_URL}/api/reviews`,
        { solicitorId, rating, review },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setRating(0);
      setReview("");
      fetchReviews();
      toast.success("Review submitted successfully!");
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to submit review";

      if (message === "You have already reviewed this solicitor") {
        toast.warning("You have already submitted a review for this solicitor");
      } else {
        toast.error(message);
      }
    }
  };

  /* ======================
     UPDATE REVIEW
  ====================== */
  const updateReview = async (reviewId) => {
    if (!editRating || editRating === 0) {
      toast.error("Please select a rating");
      return;
    }

    if (!editReview.trim()) {
      toast.error("Please write your review");
      return;
    }

    try {
      await axios.put(
        `${API_BASE_URL}/api/reviews/${reviewId}`,
        { rating: editRating, review: editReview },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setEditingId(null);
      fetchReviews();
      toast.success("Review updated successfully!");
    } catch (error) {
      toast.error("Failed to update review");
    }
  };

  /* ======================
     DELETE REVIEW WITH SWEET ALERT
  ====================== */
  const deleteReview = async (reviewId) => {
    const result = await Swal.fire({
      title: "Delete Review?",
      text: "Are you sure you want to delete this review? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f56565",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        popup: 'legal-swal-popup',
        title: 'legal-swal-title',
        confirmButton: 'legal-swal-confirm',
        cancelButton: 'legal-swal-cancel'
      }
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(
          `${API_BASE_URL}/api/reviews/${reviewId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        fetchReviews();
        Swal.fire({
          title: "Deleted!",
          text: "Your review has been deleted.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
          customClass: {
            popup: 'legal-swal-popup success',
            title: 'legal-swal-title'
          }
        });
      } catch (error) {
        Swal.fire({
          title: "Error!",
          text: "Failed to delete review",
          icon: "error",
          customClass: {
            popup: 'legal-swal-popup error',
            title: 'legal-swal-title'
          }
        });
      }
    }
  };

  /* ======================
     RENDER STARS COMPONENT
  ====================== */
  const renderStars = (currentRating, hoverRating, setRatingFn, setHoverFn, size = 20) => {
    return (
      <div className="review-stars-container">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className="review-star-btn"
            onClick={() => setRatingFn(star)}
            onMouseEnter={() => setHoverFn && setHoverFn(star)}
            onMouseLeave={() => setHoverFn && setHoverFn(0)}
          >
            {(hoverRating || currentRating) >= star ? (
              <FaStar className="review-star-filled" size={size} />
            ) : (
              <FaRegStar className="review-star-empty" size={size} />
            )}
          </button>
        ))}
        <span className="review-rating-text">
          {currentRating || hoverRating ? `${currentRating || hoverRating}.0` : "0.0"}
        </span>
      </div>
    );
  };

  /* ======================
     CALCULATE AVERAGE RATING
  ====================== */
  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : "0.0";

  const totalReviews = reviews.length;

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <div className="legal-reviews-container">
        {/* Reviews Header */}
        <div className="legal-reviews-header">
          <div className="legal-reviews-title-section">
            <h3 className="legal-reviews-title">Client Testimonials</h3>
            <p className="legal-reviews-subtitle">What clients say about their experience</p>
          </div>

          <div className="legal-reviews-stats">
            <div className="legal-rating-summary">
              <div className="legal-average-rating">{averageRating}</div>
              <div className="legal-stars-display">
                {renderStars(Math.round(parseFloat(averageRating)), 0, null, null, 16)}
              </div>
              <div className="legal-total-reviews">{totalReviews} reviews</div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="legal-reviews-loading">
            <div className="legal-loading-spinner"></div>
            <span>Loading reviews...</span>
          </div>
        )}

        {/* Reviews List */}
        <div className="legal-reviews-list">
          {reviews.map((r) => (
            <div key={r._id} className="legal-review-card">
              <div className="legal-review-header">
                <div className="legal-reviewer-info">
                  <div className="legal-reviewer-avatar">
                    {r.userId?.userProfile?.photo ? (
                      <img
                        src={r.userId.userProfile.photo}
                        alt={r.userName}
                        className="legal-reviewer-image"
                      />
                    ) : (
                      <FaUser className="legal-reviewer-placeholder" />
                    )}
                  </div>
                  <div className="legal-reviewer-details">
                    <h4 className="legal-reviewer-name">{r.userName}</h4>
                    <div className="legal-review-date">
                      {new Date(r.createdAt).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </div>
                  </div>
                </div>

                {user && user._id === r.userId?._id && (
                  <div className="legal-review-actions">
                    {editingId === r._id ? (
                      <button
                        className="legal-action-btn legal-action-cancel"
                        onClick={() => setEditingId(null)}
                      >
                        <FaTimes size={14} />
                      </button>
                    ) : (
                      <>
                        <button
                          className="legal-action-btn legal-action-edit"
                          onClick={() => {
                            setEditingId(r._id);
                            setEditRating(r.rating);
                            setEditReview(r.review);
                          }}
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          className="legal-action-btn legal-action-delete"
                          onClick={() => deleteReview(r._id)}
                        >
                          <FaTrash size={14} />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {editingId === r._id ? (
                <div className="legal-edit-review-form">
                  <div className="legal-edit-rating">
                    {renderStars(editRating, editHoverRating, setEditRating, setEditHoverRating, 24)}
                  </div>
                  <textarea
                    className="legal-edit-textarea"
                    value={editReview}
                    onChange={(e) => setEditReview(e.target.value)}
                    placeholder="Edit your review..."
                    rows={4}
                  />
                  <div className="legal-edit-actions">
                    <button
                      className="legal-save-btn"
                      onClick={() => updateReview(r._id)}
                    >
                      <FaCheck size={16} />
                      <span>Save Changes</span>
                    </button>
                    <button
                      className="legal-cancel-btn"
                      onClick={() => setEditingId(null)}
                    >
                      <FaTimes size={16} />
                      <span>Cancel</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="legal-review-content">
                  <div className="legal-review-rating">
                    {renderStars(r.rating, 0, null, null, 18)}
                  </div>
                  <p className="legal-review-text">{r.review}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add Review Form */}
        {user && (
          <div className="legal-add-review-card">
            <div className="legal-review-form-header">
              <h4 className="legal-form-title">Share Your Experience</h4>
              <p className="legal-form-subtitle">Rate your experience with this solicitor</p>
            </div>

            <div className="legal-review-form">
              <div className="legal-form-rating">
                <label className="legal-form-label">Your Rating</label>
                {renderStars(rating, hoverRating, setRating, setHoverRating, 28)}
              </div>

              <div className="legal-form-textarea">
                <label className="legal-form-label">Your Review</label>
                <textarea
                  className="legal-review-textarea"
                  value={review}
                  onChange={(e) => setReview(e.target.value)}
                  placeholder="Share your experience... What did you appreciate? How was the service?"
                  rows={5}
                />
                <div className="legal-char-count">
                  {review.length} / 1000 characters
                </div>
              </div>

              <button
                className="legal-submit-review-btn"
                onClick={submitReview}
                disabled={!rating || !review.trim() || loading}
              >
                <FaPaperPlane className="legal-submit-icon" />
                <span>Submit Review</span>
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && reviews.length === 0 && (
          <div className="legal-no-reviews">
            <div className="legal-no-reviews-icon">
              <FaRegStar size={48} />
            </div>
            <h4 className="legal-no-reviews-title">No Reviews Yet</h4>
            <p className="legal-no-reviews-text">
              Be the first to share your experience with this solicitor
            </p>
            {!user && (
              <p className="legal-no-reviews-login">
                Please login to submit a review
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );
}